import React, { useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { useTheme } from '@material-ui/core/styles';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';

import GetPluginspaceTheme from '~/util/PluginspaceTheme';

import { ChatCardContainer } from '../../Dashboard/styles';
import { buildBatteryByMode } from './cleaningReportBatteryMetrics';
import { getCleaningReportChartColors } from './cleaningReportUtils';

function formatPct(value) {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    const n = Number(value);
    return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}

function BatteryBarTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};
    return (
        <div className="jr-card p-2 mb-0">
            <div>
                <strong>{row.name || row.date}</strong>
            </div>
            <div>{formatPct(row.avg)}</div>
            {row.tasks != null ? (
                <div className="text-muted">{row.tasks}</div>
            ) : null}
        </div>
    );
}

BatteryBarTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({})),
};

BatteryBarTooltip.defaultProps = {
    active: false,
    payload: [],
};

function CleaningReportBatteryByModeCard({ list, localeCode }) {
    const theme = useTheme();
    const colors = GetPluginspaceTheme(theme);
    const { dryColor, wetColor } = getCleaningReportChartColors(colors);
    const modeColors = { 2: dryColor, 1: wetColor };

    const settings = useSelector(state => state.settings || null);
    const darkMode = !!(settings && settings.dark_mode);
    const tickFill = darkMode ? '#bdbdbd' : '#616161';
    const gridStroke = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

    const byMode = useMemo(() => buildBatteryByMode(list, localeCode), [
        list,
        localeCode,
    ]);

    if (!Array.isArray(list) || list.length === 0) {
        return null;
    }

    const chartHeight = 260;

    return (
        <div className="col-md-6 col-12 mb-5 dash-card">
            <ChatCardContainer>
                <ChartCard
                    className="animated slideInFromLeft animation-duration-3"
                    title={
                        <LocaleMessage msg="page.robots.cleaning_report.battery.by_mode.title" />
                    }
                    value=""
                    desc={
                        <LocaleMessage msg="page.robots.cleaning_report.battery.by_mode.desc" />
                    }
                >
                    <div className="row">
                        <div className="col-12 mb-3 area-chart-container">
                            <ResponsiveContainer
                                className="card-img-bottom overflow-hidden animated slideInFromLeft animation-duration-5"
                                width="100%"
                                height={chartHeight}
                            >
                                <BarChart
                                    data={byMode}
                                    margin={{
                                        top: 24,
                                        right: 16,
                                        left: 8,
                                        bottom: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        vertical={false}
                                        stroke={gridStroke}
                                    />
                                    <XAxis
                                        dataKey="name"
                                        tick={{
                                            fontSize: 11,
                                            fill: tickFill,
                                        }}
                                    />
                                    <YAxis
                                        tick={{
                                            fontSize: 11,
                                            fill: tickFill,
                                        }}
                                        tickFormatter={v => `${v}%`}
                                        width={44}
                                    />
                                    <Tooltip content={<BatteryBarTooltip />} />
                                    <Bar
                                        dataKey="avg"
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={72}
                                    >
                                        {byMode.map(row => (
                                            <Cell
                                                key={`mode-${row.mode}`}
                                                fill={
                                                    modeColors[row.mode] ||
                                                    wetColor
                                                }
                                            />
                                        ))}
                                        <LabelList
                                            dataKey="avg"
                                            position="top"
                                            formatter={v =>
                                                v ? formatPct(v) : ''
                                            }
                                            style={{
                                                fontSize: 12,
                                                fill: tickFill,
                                            }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </ChartCard>
            </ChatCardContainer>
        </div>
    );
}

CleaningReportBatteryByModeCard.propTypes = {
    list: PropTypes.arrayOf(PropTypes.shape({})),
    localeCode: PropTypes.string,
};

CleaningReportBatteryByModeCard.defaultProps = {
    list: [],
    localeCode: 'pt_BR',
};

export default injectIntl(CleaningReportBatteryByModeCard);
